import { Schema } from "@hypertune/sdk/src/shared/types";
import {
  analyticsAggregationTypes,
  AnalyticsAggregation,
  AnalyticsAggregationType,
  ExpressionField,
  AnalyticsField,
} from "@hypertune/shared-internal/src/types";
import { useCallback, useMemo, useState } from "react";
import {
  getAllValuePathsForObject,
  plansPath,
} from "@hypertune/shared-internal";
import { Pencil } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import toStartCase from "@hypertune/sdk/src/shared/helpers/toStartCase";
import Button from "../../../../components/buttons/Button";
import { intentPrimaryHex, plusSymbol } from "../../../../lib/constants";
import Modal from "../../../../components/Modal";
import ConfigSection from "./ConfigSection";
import Dropdown, { LabeledOption } from "../../../../components/Dropdown";
import toLabeledOption from "../../../../lib/toLabeledOption";
import DeleteButton from "../../../../components/buttons/DeleteButton";
import TextInput from "../../../../components/input/TextInput";
import { getStartCasePayloadPathString } from "../../../../lib/getPayloadPathString";
import isNumericValueType from "../../../../lib/isNumericValueType";
import { useHypertune } from "../../../../generated/hypertune.react";
import ModalWithContent from "../../../../components/ModalWithContent";
import ConfigItem from "./ConfigItem";
import Label from "../../../../components/Label";

export default function SelectAggregation({
  schema,
  payloadObjectTypeName,
  derivedFields,
  aggregations,
  setAggregations,
  canEdit,
}: {
  schema: Schema;
  payloadObjectTypeName: string | null;
  derivedFields: ExpressionField[];
  aggregations?: AnalyticsAggregation[];
  setAggregations: (
    newAggregations: AnalyticsAggregation[] | undefined
  ) => void;
  canEdit: boolean;
}): React.ReactElement | null {
  const navigate = useNavigate();
  const hypertune = useHypertune();
  const canUseAggregations = hypertune
    .features()
    .analyticsAggregationsEnabled({ fallback: false });

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [draftAggregationIndex, setDraftAggregationIndex] = useState<
    number | null
  >(null);

  const options: LabeledOption<AnalyticsField>[] = useMemo(
    () =>
      payloadObjectTypeName
        ? getAllValuePathsForObject(
            schema,
            payloadObjectTypeName,
            isNumericValueType
          )
            .map<LabeledOption<AnalyticsField>>((payloadPath) => ({
              value: { type: "payloadPath", payloadPath },
              label: getStartCasePayloadPathString(payloadPath),
            }))
            .concat(
              derivedFields
                .filter(({ valueType }) => isNumericValueType(valueType))
                .map<LabeledOption<AnalyticsField>>(({ name }) => ({
                  value: { type: "derivedField", fieldName: name },
                  label: toStartCase(name),
                }))
            )
        : [],
    [schema, payloadObjectTypeName, derivedFields]
  );

  if (options.length === 0) {
    return null;
  }

  return (
    <ConfigSection className="mt-4" title="Aggregations">
      {aggregations && aggregations?.length > 0 ? (
        <>
          {aggregations.map(({ type, name, data }, index) => (
            <ConfigItem
              key={`${type}-${data.type}-${data.type === "payloadPath" ? data.payloadPath.join("-") : data.fieldName}`}
              className="mb-2"
            >
              <DeleteButton
                size="x-small"
                disabled={!canEdit}
                onClick={() =>
                  setAggregations([
                    ...aggregations.slice(0, index),
                    ...aggregations.slice(index + 1),
                  ])
                }
              />
              <Button
                size="x-small"
                weight="minimal"
                intent="primary"
                disabled={!canEdit}
                icon={<Pencil color={intentPrimaryHex} size={12} />}
                onClick={() => setDraftAggregationIndex(index)}
              />
              <Label
                type="title4"
                className="scrollbar-hide overflow-x-auto whitespace-nowrap"
              >
                {name}
              </Label>
            </ConfigItem>
          ))}
        </>
      ) : null}
      <Button
        intent="primary"
        weight="minimal"
        size="default"
        disabled={!canEdit}
        icon={plusSymbol}
        text="Aggregation"
        className="mb-2"
        onClick={
          canUseAggregations
            ? () => setDraftAggregationIndex(aggregations?.length ?? 0)
            : () => setShowUpgradeModal(true)
        }
      />
      {draftAggregationIndex !== null && (
        <AggregationModal
          options={options}
          draftIndex={draftAggregationIndex}
          aggregations={aggregations ?? []}
          setAggregations={setAggregations}
          onClose={() => setDraftAggregationIndex(null)}
        />
      )}
      {showUpgradeModal && (
        <ModalWithContent
          content={hypertune
            .content()
            .plans()
            .analyticsAddAggregationModal()
            .get()}
          onClose={() => setShowUpgradeModal(false)}
          onSave={() => navigate(plansPath)}
        />
      )}
    </ConfigSection>
  );
}

function AggregationModal({
  options,
  draftIndex,
  aggregations,
  setAggregations,
  onClose,
}: {
  options: LabeledOption<AnalyticsField>[];
  draftIndex: number;
  aggregations: AnalyticsAggregation[];
  setAggregations: (
    newAggregations: AnalyticsAggregation[] | undefined
  ) => void;
  onClose: () => void;
}): React.ReactElement | null {
  const isEditing = draftIndex < aggregations.length;

  const draftBase = aggregations?.[draftIndex];
  const [name, setName] = useState(draftBase?.name ?? "");
  const [data, setData] = useState<AnalyticsField>(
    draftBase?.data ?? options[0].value
  );
  const [type, setType] = useState<AnalyticsAggregationType>(
    draftBase?.type ?? "sum"
  );
  const onSave = useCallback(() => {
    if (!name) {
      return;
    }
    setAggregations([
      ...aggregations.slice(0, draftIndex),
      { type, name, data },
      ...aggregations.slice(draftIndex + 1),
    ]);
    onClose();
  }, [onClose, setAggregations, aggregations, draftIndex, type, name, data]);

  return (
    <Modal
      title={`${isEditing ? "Edit" : "Add"} aggregation`}
      onClose={onClose}
      saveWeight="filled"
      saveDisabled={!name}
      onSave={onSave}
    >
      <TextInput
        value={name}
        onChange={setName}
        readOnly={false}
        placeholder="Set name for this aggregation"
        focusOnMount
        onEnter={onSave}
        label="Name"
        labelVariant="muted"
        style={{ marginBottom: 12 }}
      />
      <Dropdown<AnalyticsField>
        options={{ type: "options", options }}
        value={{
          value: data,
          label:
            data.type === "payloadPath"
              ? getStartCasePayloadPathString(data.payloadPath)
              : toStartCase(data.fieldName),
        }}
        onChange={(option) => {
          if (option) {
            setData(option.value);
          }
        }}
        placeholder=""
        noOptionsMessage=""
        height={30}
        label="Path"
        labelVariant="muted"
        style={{ marginBottom: 12 }}
      />
      <Dropdown<AnalyticsAggregationType>
        options={{
          type: "options",
          options: analyticsAggregationTypes.map((option) =>
            toLabeledOption(option)
          ),
        }}
        value={toLabeledOption(type)}
        onChange={(option) => {
          if (option) {
            setType(option.value);
          }
        }}
        placeholder=""
        noOptionsMessage=""
        height={30}
        label="Type"
        labelVariant="muted"
      />
    </Modal>
  );
}
